import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useLazyQuery, useApolloClient } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';
import { type DocumentNode, getOperationAST } from 'graphql';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { GenericGroupVariables } from '../../common/types';
import { useRefreshInterval, type RecoveryReturn } from '../pagination';

const MAX_ITEMS_PER_PAGE = 50;

const getOperationName = (query: DocumentNode): string => {
	const operation = getOperationAST(query, undefined);
	return (operation && operation.name && operation.name.value) || '';
};

// Reloads the whole list and replaces the main list after completed
export const useGroupRecovery = <
	TVariables extends GenericGroupVariables,
	TGroup extends {
		nodes: unknown;
		pageInfo?: {
			hasNextPage: boolean | null;
			endCursor: string | null;
		} | null;
	},
	TGroups extends {
		readonly groups: TGroup | null;
	},
	TBackgroundNodes,
	TBackgroundGroup extends {
		nodes: ReadonlyArray<TBackgroundNodes | null> | null;
		pageInfo?: {
			hasNextPage: boolean | null;
			endCursor: string | null;
		} | null;
	},
	TBackgroundGroups extends {
		readonly backgroundGroups: TBackgroundGroup | null;
	},
>({
	settings,
	queryList,
	queryBackgroundList,
	variables,
	onError,
}: {
	settings: {
		isVisible: boolean;
		updateInterval?: number;
		isDisabled?: boolean;
	};
	queryList: DocumentNode;
	queryBackgroundList: DocumentNode;
	variables: TVariables;
	onError: (arg1: ApolloError) => void;
}): RecoveryReturn => {
	const analyticsTaskId = getOperationName(queryBackgroundList);
	const analyticsTaskName = 'groupsBackgroundRefetch';

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const backgroundQueryVariables = useMemo(
		() => ({
			...variables,
			first: MAX_ITEMS_PER_PAGE,
		}),
		[variables],
	);

	const client = useApolloClient();

	const [fetchListInBackground, { loading, data, fetchMore }] = useLazyQuery<
		TBackgroundGroups,
		TVariables
	>(queryBackgroundList, {
		fetchPolicy: 'network-only',
		onError,
	});

	const [hasStarted, setHasStarted] = useState(false);
	const [hasFinished, setHasFinished] = useState(false);
	const [refreshCount, setRefreshCount] = useState(0);
	const [currentQuery, setCurrentQuery] = useState<TVariables['jql']>();
	const [currentGroup, setCurrentGroup] = useState<TVariables['groupByFieldId']>();

	const listEndCursor = useRef<string | null | undefined>(undefined);
	const { hasNextPage, endCursor } = data?.backgroundGroups?.pageInfo || {};
	const prevEndCursor: string | null | undefined = usePreviousWithInitial(endCursor);

	// refetch all pages until the current endCursor: if we have 5 pages in total, list endCursor is on page 2,
	// then background refresh will pull first 2 pages only
	useEffect(() => {
		if (
			hasStarted &&
			hasNextPage &&
			// query pages one-by-one until we reach the current page
			endCursor !== listEndCursor.current &&
			// query pages only when endCursor changes,
			// otherwise it might query the same page multiple times due to re-renders of useListRecovery
			endCursor !== prevEndCursor
		) {
			fetchMore({
				variables: { ...backgroundQueryVariables, after: endCursor },
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult) return prev;

					const updatedNodes = [
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						...((prev as TBackgroundGroups)?.backgroundGroups?.nodes || []),
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						...((fetchMoreResult as TBackgroundGroups)?.backgroundGroups?.nodes || []),
					];
					return {
						backgroundGroups: {
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							...(fetchMoreResult as TBackgroundGroups).backgroundGroups,
							nodes: updatedNodes,
						},
					};
				},
			});
		}
	}, [
		hasStarted,
		hasNextPage,
		endCursor,
		prevEndCursor,
		fetchListInBackground,
		backgroundQueryVariables,
		fetchMore,
	]);

	// abort background operations if a new query is provided
	useEffect(() => {
		if (
			!hasStarted ||
			(variables.jql === currentQuery && variables.groupByFieldId === currentGroup)
		) {
			return;
		}

		fireOperationalAnalytics(createAnalyticsEvent({}), 'ui taskAbort', {
			task: analyticsTaskName,
			taskId: analyticsTaskId,
			reason: 'query has changed',
		});

		setHasFinished(true);
		setHasStarted(false);
		setCurrentQuery(undefined);
		setCurrentGroup(undefined);
	}, [
		analyticsTaskId,
		createAnalyticsEvent,
		currentGroup,
		currentQuery,
		hasStarted,
		variables,
		variables.groupByFieldId,
		variables.jql,
	]);

	// replace list items with backgroundList items so the main view will update
	useEffect(() => {
		if (hasStarted && !hasFinished && !loading && listEndCursor.current === endCursor) {
			try {
				const backgroundListData = client.readQuery<TBackgroundGroups, TVariables>({
					query: queryBackgroundList,
					variables: backgroundQueryVariables,
				});

				const getCachedListData = () => {
					try {
						const read = client.readQuery<TGroups, TVariables>({
							query: queryList,
							variables,
						});

						fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheRead taskSuccess', {
							task: analyticsTaskName,
							taskId: analyticsTaskId,
						});

						return read;
					} catch (e) {
						fireErrorAnalytics({
							error: e instanceof Error ? e : new Error('Unknown cache read error'),
							meta: {
								id: 'useGroupRecovery',
								packageName: 'jiraBusinessListHandlers',
								teamName: 'keystone',
							},
							sendToPrivacyUnsafeSplunk: true,
							skipSentry: true,
							attributes: {
								task: analyticsTaskName,
								taskId: analyticsTaskId,
								query: JSON.stringify(queryList),
								variables: JSON.stringify(variables),
								eventName: 'apolloCacheRead taskFail',
								error: JSON.stringify(e),
							},
						});

						return null;
					}
				};

				const listData = getCachedListData();

				setRefreshCount(refreshCount + 1);
				setHasFinished(true);
				setHasStarted(false);
				setCurrentQuery(undefined);
				setCurrentGroup(undefined);

				if (
					!backgroundListData ||
					backgroundListData.backgroundGroups == null ||
					!listData ||
					listData.groups == null ||
					!Array.isArray(backgroundListData.backgroundGroups.nodes) ||
					!Array.isArray(listData.groups.nodes)
				) {
					throw new Error('Conditions not met');
				}

				const { backgroundGroups } = backgroundListData;

				if (backgroundGroups.nodes == null) {
					return;
				}
				client.writeQuery({
					query: queryList,
					variables,
					data: {
						groups: {
							...backgroundGroups,
							nodes: backgroundGroups.nodes,
						},
					},
				});

				fireOperationalAnalytics(createAnalyticsEvent({}), 'ui taskSuccess', {
					task: analyticsTaskName,
					taskId: analyticsTaskId,
					details: `${backgroundGroups.nodes.length} items updated`,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				fireOperationalAnalytics(createAnalyticsEvent({}), 'ui taskFail', {
					task: analyticsTaskName,
					taskId: analyticsTaskId,
					error: `Failed to refresh data in the background (${e.name || 'error'})`,
				});
			}
		}
	}, [
		hasStarted,
		client,
		loading,
		hasFinished,
		refreshCount,
		backgroundQueryVariables,
		queryList,
		queryBackgroundList,
		variables,
		createAnalyticsEvent,
		analyticsTaskId,
		endCursor,
	]);

	const refreshList = useCallback(() => {
		if ((hasStarted && !hasFinished) || settings.isDisabled) {
			return;
		}

		const listData = client.readQuery<TGroups, TVariables>({
			query: queryList,
			variables,
		});
		// store current list endCursor so that we know at what page to stop backgroundList fetching
		listEndCursor.current = listData?.groups?.pageInfo?.endCursor;

		fetchListInBackground({
			variables: backgroundQueryVariables,
		});
		setCurrentQuery(variables.jql);
		setCurrentGroup(variables.groupByFieldId);
		setHasStarted(true);
		setHasFinished(false);

		fireOperationalAnalytics(createAnalyticsEvent({}), 'ui taskStart', {
			task: analyticsTaskName,
			taskId: analyticsTaskId,
		});
	}, [
		analyticsTaskId,
		backgroundQueryVariables,
		client,
		createAnalyticsEvent,
		fetchListInBackground,
		hasFinished,
		hasStarted,
		queryList,
		settings.isDisabled,
		variables,
	]);

	const isRefreshing = hasStarted && !hasFinished;

	useRefreshInterval(
		refreshList,
		// it won't trigger a refresh if another has been triggered
		// it won't trigger refresh if the tab is invisible
		settings.isVisible && !isRefreshing,
		settings.updateInterval,
	);

	return {
		isRefreshing,
		refreshCount,
		refreshList,
	};
};
